import axios from "axios";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import Vue from "vue";
import { isNativeMobileApp } from "./runtime";
import { mobileBrand } from "./brand";

export const mobileAppUpdate = Vue.observable({
  visible: false,
  forceUpdate: false,
  latestVersion: "",
  minimumVersion: "",
  storeUrl: "",
});

let initialized = false;
let checkInProgress = null;
let dismissedVersion = "";

export function compareNumericVersions(left, right) {
  const parse = value => {
    const text = String(value || "").trim();
    if (!/^\d+(?:\.\d+){1,3}$/.test(text)) return null;
    return text.split(".").map(part => Number(part));
  };
  const leftParts = parse(left);
  const rightParts = parse(right);
  if (!leftParts || !rightParts) return null;
  const length = Math.max(leftParts.length, rightParts.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (leftParts[index] || 0) - (rightParts[index] || 0);
    if (difference) return difference > 0 ? 1 : -1;
  }
  return 0;
}

export async function checkForMobileAppUpdate() {
  if (!isNativeMobileApp || !Capacitor.isNativePlatform()) return null;
  if (checkInProgress) return checkInProgress;
  checkInProgress = (async () => {
    try {
      const appInfo = await App.getInfo();
      const platform = Capacitor.getPlatform();
      if (!["android", "ios"].includes(platform)) return null;
      const response = await axios.get(`${process.env.VUE_APP_SERVER_URL}/mobile/${mobileBrand.key}/app-update`, {
        params: { platform, version: appInfo.version, build: appInfo.build },
        timeout: 10000,
      });
      const payload = response.data && response.data.data ? response.data.data : response.data;
      if (!payload || typeof payload !== "object") return null;
      const latestVersion = String(payload.latestVersion || "").trim();
      const minimumVersion = String(payload.minimumVersion || "").trim();
      const latestComparison = compareNumericVersions(appInfo.version, latestVersion);
      const minimumComparison = compareNumericVersions(appInfo.version, minimumVersion);
      if (latestComparison == null || minimumComparison == null || !/^https:\/\//i.test(String(payload.storeUrl || ""))) return null;
      const forceUpdate = payload.forceUpdate === true || minimumComparison < 0;
      const updateAvailable = forceUpdate || payload.updateAvailable === true || latestComparison < 0;
      if (!updateAvailable || (!forceUpdate && dismissedVersion === latestVersion)) return null;
      Object.assign(mobileAppUpdate, {
        visible: true,
        forceUpdate,
        latestVersion,
        minimumVersion,
        storeUrl: String(payload.storeUrl),
      });
      return mobileAppUpdate;
    } catch (error) {
      return null;
    } finally {
      checkInProgress = null;
    }
  })();
  return checkInProgress;
}

export function dismissMobileAppUpdate() {
  if (mobileAppUpdate.forceUpdate) return;
  dismissedVersion = mobileAppUpdate.latestVersion;
  mobileAppUpdate.visible = false;
}

export async function initializeMobileAppUpdateCheck() {
  if (!isNativeMobileApp || !Capacitor.isNativePlatform() || initialized) return;
  initialized = true;
  await App.addListener("appStateChange", ({ isActive }) => {
    if (isActive) checkForMobileAppUpdate();
  });
  checkForMobileAppUpdate();
}
